/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import ScaleLoader from 'react-spinners/ScaleLoader';
import styled from 'styled-components';
import {
  ErrorText,
  Link,
  Text
} from '@comicrelief/component-library';
import {
  JustGivingPageLinkLoaderWrapper,
  JustGivingRegisterHeading,
  ActivateJustGivingButton,
  JustGivingPill,
  ErrorIcon
} from '../FundraiserSignup.style';
import {
  CAMPAIGN_STRING,
  FUNDRAISING_GROUPS,
  SCHOOL_SEGMENTS,
  FIELDS
} from '../constants';
import FundraiserSignupContext from '../context/FundraiserSignupContext';
import JustGivingWhiteLogo from '../assets/JustGivingWhiteLogo.svg';
import ErrorAlertIcon from '../assets/error-alert-icon-red.svg';

export const ErrorLink = styled(Link)`
  font-weight: bold;
`;

const PageLinkLoader = ({ UUID = null, campaignCode, campaignUrl }) => {
  const { getValues } = useContext(FundraiserSignupContext);

  // While we can't use this as a timeout flag with the
  // async getSubmissionData function, we still need it
  // to retrigger the rerender for the msg
  const [showTimeoutMsg, setShowTimeoutMsg] = useState(false);
  const [isErroring, setIsErroring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [jGPageURL, setJGPageURL] = useState(null);
  const [formattedCampaignCode, setFormattedCampaignCode] = useState(null);
  const [fallbackCampaignUrl, setFallbackCampaignUrl] = useState(null);

  // To avoid weird problems using useState within the async,
  // use a global var for both the flag AND the timer
  let hasTimedOut = false;
  let totalTimer;

  // Set a realistic retry period:
  const retryDuration = 5000;

  // How long we should keep retrying until throwing in the towel:
  const attemptDuration = 45000;

  // To allow us to cancel the GET...
  const cancelTokenSource = axios.CancelToken.source();

  // NB This function contains hardcoded 2027 URLs, this file will need changing each year!
  const getFallbackCampaignUrl = () => {
    try {
      const fundraisingGroup = getValues(FIELDS.GROUP);
      const fundraisingSegment = getValues(FIELDS.SEGMENT);

      let fallbackUrl;

      switch (fundraisingGroup) {
        case FUNDRAISING_GROUPS.WORK:
          fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027-workplaces';
          break;

        case FUNDRAISING_GROUPS.SCHOOL:
          switch (fundraisingSegment) {
            case SCHOOL_SEGMENTS.NURSERY.value:
              fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027-earlyyears';
              break;
            case SCHOOL_SEGMENTS.PRIMARY.value:
            case SCHOOL_SEGMENTS.OTHER.value: // As per serverless-fundraiser-signup config
              fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027-primary';
              break;
            case SCHOOL_SEGMENTS.SECONDARY.value:
              fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027-secondary';
              break;
            default:
              fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027?utm_medium=CA&utm_source=CL';
          }
          break;

        default:
          // For PUBLIC group or any other case, use the default fallback
          fallbackUrl = 'https://www.justgiving.com/campaign/rednoseday2027?utm_medium=CA&utm_source=CL';
      }
      return fallbackUrl;
    } catch (error) {
      return 'https://www.justgiving.com/campaign/rednoseday2027?utm_medium=CA&utm_source=CL';
    }
  };

  // As a 404 'error' response from our getSubmissionData function is possible before
  // the user data gets everywhere it needs to, let's handle it accordingly:
  const handleError = (error) => {
    if (error && error.response && error.response.status
      && error.response.status === 404) {
      if (!hasTimedOut) waitAndRetry();
    } else {
      // Else, it's a real error, so handle appropriately:
      setIsErroring(true);
      setIsLoading(false);
      // Handle a *server* timeout too, just in case
      if (error.code === 'ECONNABORTED') {
        hasTimedOut = true;
        setShowTimeoutMsg(true);
      }
    }
  };

  // Try again after the retryDuration period
  const waitAndRetry = () => {
    setTimeout(() => {
      getSubmissionData();
    }, retryDuration);
  };

  // Wraps the Axios GET with the associated actions to make it easily reusable
  const getSubmissionData = async () => {
    try {
      await axios.get(`${process.env.GATSBY_FSU_SUBMIT_URL}/signup/${UUID}`, { timeout: 30000, cancelToken: cancelTokenSource.token })
        .then((response) => {
          // If we've got a response but no JG page URL yet
          if (response.data.data.jg_page_claim_url === null) {
            // Only rerun the whole thing if we're not out of time:
            if (!hasTimedOut) waitAndRetry();
          } else {
            // Else, we DO have an URL so save it to the state to use in render, and update our flag
            setJGPageURL(response.data.data.jg_page_claim_url);
            setIsLoading(false);
            // Cancel the totalTimeout timer as we're successful
            clearTimeout(totalTimer);
          }
        });
    } catch (error) {
      // This can either be a 404 'still can't find the user's
      // submission' error, or something else entirely
      handleError(error);
    }
  };

  // Triggered on initial mount
  const totalTimeout = () => {
    totalTimer = setTimeout(() => {
      hasTimedOut = true;
      setShowTimeoutMsg(true);
      setIsLoading(false);
      // Cancel GET request:
      cancelTokenSource.cancel();
    }, attemptDuration);
  };

  // Update our UX flags, start timer and trigger main function on initial mount:
  useEffect(() => {
    totalTimeout();
    setIsLoading(true);
    getSubmissionData();

    // Short-term fix to allow for a little more flexibility.
    // If we don't have a copy string to use, just fallback
    // to nothing as the copy still makes sense without it.
    const formatted = CAMPAIGN_STRING(campaignCode) || '';
    setFormattedCampaignCode(formatted);

    const fallbackUrl = getFallbackCampaignUrl();
    setFallbackCampaignUrl(fallbackUrl);
  }, []);

  return (
    <JustGivingPageLinkLoaderWrapper>
      <JustGivingPill>
        <img src={JustGivingWhiteLogo} alt="JustGiving" />
      </JustGivingPill>
      {/* Inform the user something's happening: */}
      {(isLoading && !isErroring) && (
        <>
          <JustGivingRegisterHeading tag="p" size="l" weight="bold">
            Registering your
            {' '}
            {formattedCampaignCode}
            {' '}
            JustGiving page...
          </JustGivingRegisterHeading>

          <Text tag="p">
            This could take up to 30 seconds or so. Please don&apos;t refresh or leave the page.
          </Text>

          <ScaleLoader height={32} width={4} loading={isLoading} />
        </>
      )}

      {/* If we've been returned a page URL, show it: */}
      {(!isLoading && !isErroring && jGPageURL) && (
      <>
        <JustGivingRegisterHeading tag="p" weight="bold">
          Your JustGiving page is ready to activate!
        </JustGivingRegisterHeading>
        <Text tag="p">
          JustGiving is the easiest and safest way to fundraise online.
          Once your page is activated you can add your own photos, update your story and change your
          fundraising target to help get those donations in.
        </Text>
        <Text tag="p" size="l">
          <ActivateJustGivingButton
            href={jGPageURL}
            type="button"
            target="self"
          >
            Activate your JustGiving page
          </ActivateJustGivingButton>
        </Text>
      </>
      )}

      {/* If there's a 'proper' error thrown by handleError, let the user know: */}
      { (!isLoading && (isErroring || showTimeoutMsg)) && (
      <>
        {/* If we've hit some timeout related issue, direct the user to their inbox */}
        { showTimeoutMsg ? (
          <>
            <Text tag="p" weight="bold">
              <ErrorIcon>
                <img src={ErrorAlertIcon} alt="Error alert" />
              </ErrorIcon>
              Sorry, we were unable to create your page.
            </Text>
            <Text tag="p">
              But don&apos;t worry, you&apos;re still registered to fundraise
              with us and can create your JustGiving page manually by clicking
              the link below, and selecting the option to &apos;start fundraising&apos;.
            </Text>
            <Text tag="p">
              JustGiving is the easiest and safest way to fundraise online
              and by using the link below, we can ensure that your JustGiving
              page is successfully linked to our campaign.
            </Text>
            <Text tag="p" size="l">
              <ActivateJustGivingButton
                href={campaignUrl || fallbackCampaignUrl}
                type="button"
                target="self"
              >
                Create your JustGiving page
              </ActivateJustGivingButton>
            </Text>
          </>
        ) : (
        /* Otherwise, a more general error message */
          <ErrorText weight="bold">
            Sorry, something went wrong. Please refresh the page and try again, or contact
            {' '}
            <Link href="mailto:fundraising@comicrelief.com">fundraising@comicrelief.com</Link>
            {' '}
            if the problem persists.
          </ErrorText>
        )}
      </>
      )}
    </JustGivingPageLinkLoaderWrapper>
  );
};

PageLinkLoader.propTypes = {
  UUID: PropTypes.string,
  campaignCode: PropTypes.string.isRequired,
  campaignUrl: PropTypes.string
};

export default PageLinkLoader;
